import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

export class FeatureRoutineBundle extends Component {
  #clickHandler = this.#handleClick.bind(this);
  #buyHandler = this.#onBuy.bind(this);

  // Cached refs
  #productsListEl = /** @type {HTMLElement | null} */ (null);
  #currentPriceEl = /** @type {HTMLElement | null} */ (null);
  #buyBtn = /** @type {HTMLElement | null} */ (null);
  #errorEl = /** @type {HTMLElement | null} */ (null);
  
  // Style attrs resolved once from source elements
  #titleAttrs = 'class="summary__product-title"';
  #priceAttrs = 'class="summary__price"';
  #moneyFormat = '{{amount}}';
  #currency = 'USD';

  connectedCallback() {
    super.connectedCallback();

    this.#productsListEl = this.querySelector('.summary__products-list');
    this.#currentPriceEl = this.querySelector('.summary__price-current');
    this.#buyBtn = this.querySelector('.custom-figma-btn') || this.querySelector('.summary__button');
    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.#currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';

    // Resolve style attrs once from source elements
    const titleSrc = this.querySelector('.js-source-product-title .text-block');
    const priceSrc = this.querySelector('.js-source-product-price .text-block');
    if (titleSrc) this.#titleAttrs = `class="${titleSrc.className}" style='${titleSrc.getAttribute('style') || ''}'`;
    if (priceSrc) this.#priceAttrs = `class="${priceSrc.className}" style='${priceSrc.getAttribute('style') || ''}'`;

    this.addEventListener('click', this.#clickHandler);
    if (this.#buyBtn) this.#buyBtn.addEventListener('click', this.#buyHandler);

    this.#initSteps();

    // Delay initial update slightly to allow Theme Editor block injection
    setTimeout(() => this.#updateSummary(), 50);
  }

  #initSteps() {
    this.querySelectorAll('.feature-routine-bundle__item').forEach((item, index) => {
      const stepNumber = String(index + 1);
      const badge = item.querySelector('.routine-step-counter');
      if (badge) badge.textContent = stepNumber;

      for (const block of item.querySelectorAll('.text-block')) {
        const text = (block.textContent || '').trim();
        if (/^Step\s*\d+$/i.test(text)) {
          const inner = block.firstElementChild;
          if (inner && inner.tagName.match(/^(P|H[1-6]|SPAN)$/)) {
            inner.textContent = `Step ${stepNumber}`;
          } else {
            block.textContent = `Step ${stepNumber}`;
          }
        }
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('click', this.#clickHandler);
    if (this.#buyBtn) this.#buyBtn.removeEventListener('click', this.#buyHandler);
  }

  /** @param {Event & { target: HTMLElement }} e */
  #handleClick(e) {
    const toggleBtn = e.target.closest('.routine-bundle-toggle-btn');
    if (toggleBtn && this.contains(toggleBtn)) {
      const selected = toggleBtn.classList.toggle('is-selected');
      toggleBtn.setAttribute('aria-checked', String(selected));
      this.#updateSummary();
    }
  }

  #updateSummary() {
    // Dynamically query selected buttons in case Theme Editor injected new ones
    const selected = Array.from(this.querySelectorAll('.routine-bundle-toggle-btn.is-selected'));
    let originalTotal = 0;

    const html = selected.map((btn) => {
      const title = btn.getAttribute('data-title') || '';
      const rawPrice = btn.getAttribute('data-price');
      const price = rawPrice ? Number(rawPrice) || 0 : 0;
      originalTotal += price;
      
      return `<li class="summary__product-item">
        <span ${this.#titleAttrs}>${title}</span>
        <span ${this.#priceAttrs}>${formatMoney(price, this.#moneyFormat, this.#currency)}</span>
      </li>`;
    }).join('');

    if (this.#productsListEl) this.#productsListEl.innerHTML = html;
    
    // Default to original total
    let total = originalTotal;

    // Fetch dynamic discount parameters from data attributes
    const discountType = (this.getAttribute('data-discount-type') || '').trim().toLowerCase();
    const discountValue = Number(this.getAttribute('data-discount-value')) || 0;
    const discountMin = Number(this.getAttribute('data-discount-min')) || 1;

    // Apply dynamic discount if minimum quantity is met
    if (selected.length > 0 && selected.length >= discountMin && discountValue > 0) {
      if (discountType === 'fixed amount') {
        const discountInCents = discountValue * 100;
        total = Math.max(0, originalTotal - discountInCents);
      } else {
        const discountMultiplier = 1 - (discountValue / 100);
        total = Math.round(originalTotal * discountMultiplier);
      }
    }

    if (this.#currentPriceEl) {
      const node = this.#currentPriceEl.querySelector('.text-block') ?? this.#currentPriceEl;
      node.textContent = formatMoney(total, this.#moneyFormat, this.#currency);

      /** @type {HTMLElement | null} */
      const rightSideEl = this.querySelector('.summary__price-right');
      if (rightSideEl) {
        if (selected.length > 0 && total < originalTotal) {
          rightSideEl.style.display = 'flex';
          
          const compareAtEl = rightSideEl.querySelector('.summary__price-compare-at');
          if (compareAtEl) {
            const compareNode = compareAtEl.querySelector('.text-block') ?? compareAtEl;
            compareNode.innerHTML = formatMoney(originalTotal, this.#moneyFormat, this.#currency);
          }
          
          const saveEl = rightSideEl.querySelector('.summary__price-save');
          if (saveEl) {
            const saveNode = saveEl.querySelector('.text-block') ?? saveEl;
            saveNode.innerHTML = `Save ${formatMoney(originalTotal - total, this.#moneyFormat, this.#currency)}`;
          }
        } else {
          rightSideEl.style.display = 'none';
        }
      }
    }
  }

  /** @param {string} msg */
  #showError(msg) {
    if (!this.#errorEl) {
      this.#errorEl = this.querySelector('.summary__error-message');
    }
    if (!this.#errorEl) {
      this.#errorEl = document.createElement('div');
      this.#errorEl.className = 'summary__error-message';
      this.#errorEl.style.marginTop = '10px';
      this.#errorEl.style.textAlign = 'center';
      
      const titleSrc = this.querySelector('.js-source-product-title .text-block');
      const p = document.createElement('p');
      if (titleSrc) {
        p.className = titleSrc.className;
        p.setAttribute('style', titleSrc.getAttribute('style') || '');
      } else {
        p.className = 'text-block text-body-medium';
      }
      p.style.margin = '0';
      p.style.color = '#fff'; // Error color
      this.#errorEl.appendChild(p);
      
      const footerText = this.querySelector('.summary__footer-text');
      const btn = this.#buyBtn;
      if (footerText && footerText.parentNode) {
        footerText.parentNode.insertBefore(this.#errorEl, footerText);
      } else if (btn && btn.parentNode) {
        btn.parentNode.insertBefore(this.#errorEl, btn.nextSibling);
      } else {
        this.appendChild(this.#errorEl);
      }
    }
    
    const pNode = this.#errorEl.querySelector('p');
    if (pNode) {
      pNode.textContent = msg;
    }
    this.#errorEl.style.display = 'block';
    
    setTimeout(() => {
      if (this.#errorEl) this.#errorEl.style.display = 'none';
    }, 5000);
  }

  /** @param {Event} e */
  async #onBuy(e) {
    e.preventDefault();
    const btn = this.#buyBtn;
    if (!btn) return;

    const selectedButtons = Array.from(this.querySelectorAll('.routine-bundle-toggle-btn.is-selected'));
    const variantIds = selectedButtons
      .map((b) => b.getAttribute('data-variant-id'))
      .filter(Boolean);

    if (!variantIds.length) {
      this.#showError('Please select at least one product to continue.');
      return;
    }

    // Check if any selected product has a cart quantity limit of 1
    const hasLimited = selectedButtons.some(b => b.getAttribute('data-is-limited') === 'true');
    if (hasLimited) {
      try {
        const cartRes = await fetch(window.Theme?.routes?.cart_url || '/cart.js');
        const cart = await cartRes.json();
        
        for (const button of selectedButtons) {
          if (button.getAttribute('data-is-limited') === 'true') {
            const vId = button.getAttribute('data-variant-id');
            if (!vId) continue;
            const cartItem = cart.items.find((/** @type {any} */ item) => item.variant_id.toString() === vId.toString());
            const currentQty = cartItem ? cartItem.quantity : 0;
            if (currentQty + 1 > 1) {
              this.#showError(`You can only add 1 of "${button.getAttribute('data-title')}" to your cart.`);
              return;
            }
          }
        }
      } catch (err) {
        console.error('Failed to validate bundle quantity limits:', err);
      }
    }

    const items = variantIds.map((id) => ({ id: /** @type {string} */ (id), quantity: 1 }));
    const originalHTML = btn.innerHTML;
    btn.innerHTML = btn.dataset.addingText || 'ADDING...';
    if ('disabled' in btn) btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map(({ id }) => ({ merchandiseId: id, quantity: 1 })),
      promise: deferred.promise,
    }));

    try {
      const addRes = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const addData = await addRes.json();

      if (addData.status && addData.status !== 200) {
        this.dispatchEvent(new CartErrorEvent({
          error: addData.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: addData.description, errors: addData.errors },
        }));
        deferred.reject(new Error(addData.message));
        throw new Error(addData.message || 'Error adding to cart');
      }

      const cartData = await (await fetch(window.Theme?.routes?.cart_url || '/cart.js')).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartData),
        detail: {
          didError: false,
          items: cartData.items,
          source: 'feature-routine-bundle',
          sourceId: this.id || 'bundle',
          itemCount: items.length,
        },
      });
    } catch (err) {
      console.error('[routine-bundle]', err);
      alert('An error occurred: ' + (err instanceof Error ? err.message : err));
    } finally {
      btn.innerHTML = originalHTML;
      if ('disabled' in btn) btn.disabled = false;
      btn.setAttribute('aria-disabled', 'false');
    }
  }
}

if (!customElements.get('feature-routine-bundle')) {
  customElements.define('feature-routine-bundle', FeatureRoutineBundle);
}
